/**
 * The leader lines: the geometry that puts one on the drawing, and the layer
 * that draws every one of them.
 *
 * Apart from the drawing itself because none of it touches React state. A line
 * is decided by two points, however many bends it carries, and the box it is
 * drawn in; given those it answers the same way every time.
 */

import type { PointModel } from "@patent-studio/api-types";
import type {
  FigureSettings,
  Instance,
  NumeralStyle,
} from "@/lib/element-registry";
import { styleOf } from "@/lib/element-registry";
import type { DrawnBox } from "./drag-gesture";
import type { Placement } from "./AnnotatedDrawing";

export const percent = (fraction: number) => `${String(fraction * 100)}%`;

/**
 * How many segments a curve is drawn in.
 *
 * Even, so the halfway point of the curve is a vertex rather than a chord across it.
 */
const CURVE_SEGMENTS = 24;

/** The two ends of the arrowhead marker, one per line appearance. */
const HEAD_IDS = {
  plain: "define-head",
  chosen: "define-head-chosen",
} as const;

/**
 * A point on the Bézier the given points describe, by repeated interpolation.
 *
 * De Casteljau's construction: interpolate each neighbouring pair, then do it again on what
 * comes back, until one point is left. It answers for any number of controls, which is what
 * lets one bend and two share a renderer rather than each having its own formula.
 */
function onCurve(points: PointModel[], t: number): PointModel {
  let level = points;
  while (level.length > 1) {
    level = level.slice(1).map((to, index) => {
      const from = level[index];
      return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
      };
    });
  }
  return level[0];
}

/**
 * How much room each numeral style needs at the start of its own leader, in pixels.
 *
 * A leader is stored from the pin, and the numeral is drawn at the pin, so a leader drawn
 * from where it is stored runs underneath its own numeral. Bare digits have nothing to hide
 * that and the line crosses them; a ring has the line crossing its lower arc. Enough to
 * clear each, and no more, so the leader still visibly belongs to the numeral it names.
 *
 * In pixels because that is what it is clearing. A numeral is 26px whatever size the
 * drawing is shown at, so a clearance held as a fraction of the drawing is only ever right
 * at one width: expand the same drawing and the gap grows with it, until the leaders stop
 * short of the numerals they belong to.
 */
const NUMERAL_CLEARS: Record<NumeralStyle, number> = {
  // Nothing. The ring is opaque and is painted over the line, so the stretch that runs
  // underneath is already hidden, and trimming it back only opens a gap between the leader
  // and the circle it comes from. A filed drawing has the two touching.
  circled: 0,
  plain: 10,
};

const away = (from: PointModel, to: PointModel, box: DrawnBox) =>
  Math.hypot((to.x - from.x) * box.width, (to.y - from.y) * box.height);

/**
 * The same line with its first stretch taken off, so it starts clear of its numeral.
 *
 * Trimmed rather than redrawn from a moved start: a curve is the shape its stored bends
 * describe, and starting it somewhere else would bend it differently. So the vertices are
 * the ones the stored line passes through, with those inside the clearance dropped and the
 * first survivor pulled back onto the boundary.
 *
 * The stored pin is untouched throughout. This is where the line is drawn from, not where
 * the mark is, so nothing about a drag or a saved position changes.
 */
function clearingTheNumeral(
  drawn: PointModel[],
  style: NumeralStyle,
  box: DrawnBox | undefined,
): PointModel[] {
  const clear = NUMERAL_CLEARS[style];
  // Before the first measurement there is no distance to trim by, and a drawing with no
  // width has not been laid out. The whole line is drawn until there is.
  if (clear === 0 || box === undefined || box.width === 0 || box.height === 0)
    return drawn;
  const pin = drawn[0];
  const beyond = drawn.findIndex((point) => away(pin, point, box) >= clear);
  // A leader shorter than its own numeral is wide has nowhere to start, so it is drawn
  // whole rather than reversed through the mark it names.
  if (beyond < 1) return drawn;

  const inside = drawn[beyond - 1];
  const outside = drawn[beyond];
  const span = away(inside, outside, box);
  const wanted = clear - away(pin, inside, box);
  const at = span === 0 ? 0 : wanted / span;
  return [
    {
      x: inside.x + (outside.x - inside.x) * at,
      y: inside.y + (outside.y - inside.y) * at,
    },
    ...drawn.slice(beyond),
  ];
}

/**
 * The vertices a connector is drawn through, from the pin to the tip.
 *
 * A straight line is its two ends. A curve is the Bézier through its stored bends,
 * flattened into segments, because svg path data takes no percentages and this overlay
 * deliberately carries no viewBox: one user unit is one pixel of the box the points were
 * measured in, which is what keeps the stroke and the arrowhead undistorted whatever shape
 * the drawing is. Flattening in the same percentage space costs a few elements and keeps
 * every other coordinate in this file the one the attorney clicked.
 */
function vertices(
  instance: Instance,
  style: NumeralStyle,
  box: DrawnBox | undefined,
): PointModel[] {
  const line = instance.connector;
  if (line === null || line === undefined) return [];
  const { pin } = instance;
  if (line.shape !== "curved")
    return clearingTheNumeral([pin, line.tip], style, box);

  const { tip, controls } = line;
  // A Bézier of whatever degree the stored bends describe: one control is the quadratic,
  // two are the cubic that makes an S. De Casteljau rather than the expanded polynomials,
  // so a second bend is a longer list rather than a second formula to keep in step.
  const curve = Array.from({ length: CURVE_SEGMENTS + 1 }, (_, step) =>
    onCurve([pin, ...controls, tip], step / CURVE_SEGMENTS),
  );
  return clearingTheNumeral(curve, style, box);
}

/**
 * The head that names the part, drawn once per appearance a line can have.
 *
 * Fixed ids rather than generated ones. Two definitions of the same marker would be
 * identical, so a second drawing on the page resolving to the first one's marker draws the
 * same head.
 */
export function Arrowheads() {
  return (
    <defs>
      {Object.entries(HEAD_IDS).map(([kind, id]) => (
        <marker
          key={id}
          id={id}
          markerWidth={6}
          markerHeight={6}
          refX={5.5}
          refY={3}
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path
            d="M0,0 L6,3 L0,6 Z"
            className={kind === "chosen" ? "fill-primary" : "fill-foreground"}
          />
        </marker>
      ))}
    </defs>
  );
}

/**
 * How wide a line is to click, against the 1.5 it is to look at.
 *
 * The stroke that catches the pointer is its own invisible line over the drawn one, so the
 * target can be a comfortable size without the drawing getting heavier.
 */
const HIT_WIDTH = 14;

/** The leader lines, each running from its pin to the part it names. */
export function Connectors({
  instances,
  selectedId,
  settings,
  placing,
  onSelect,
  box,
}: {
  instances: Instance[];
  selectedId: string | undefined;
  /** The set's conventions, which each mark is drawn under unless it says otherwise. */
  settings: FigureSettings;
  /** While a placement is being made, a click on a line belongs to the drawing beneath. */
  placing: boolean;
  onSelect: (placement: Placement) => void;
  /** The drawing's live size, for the clearance that is measured in pixels. */
  box: DrawnBox | undefined;
}) {
  return (
    <svg
      className="absolute inset-0 size-full"
      // Hidden from a reader rather than from a pointer. Every mark is already reachable by
      // its pin, which is a real button carrying the numeral; the line is a second way to
      // the same choice for whoever is using a pointer, not a control of its own.
      aria-hidden
      // Only the lines that ask for it hear the pointer, which keeps this layer a renderer
      // and leaves clicks on the drawing to the drawing.
      pointerEvents="none"
    >
      <Arrowheads />
      {instances.map((instance) => {
        const drawn = vertices(instance, styleOf(instance, settings), box);
        const chosen = instance.id === selectedId;
        const headed = instance.connector?.arrowhead ?? false;
        return drawn.slice(1).map((to, index) => {
          const from = drawn[index];
          const last = index === drawn.length - 2;
          return (
            <g key={`${instance.id}:${String(index)}`}>
              {!placing && (
                <line
                  data-connector-hit
                  x1={percent(from.x)}
                  y1={percent(from.y)}
                  x2={percent(to.x)}
                  y2={percent(to.y)}
                  stroke="transparent"
                  strokeWidth={HIT_WIDTH}
                  strokeLinecap="round"
                  pointerEvents="stroke"
                  className="cursor-pointer"
                  onClick={(event) => {
                    // The drawing beneath reads a click on itself as a click on nothing and
                    // would clear the selection this just made.
                    event.stopPropagation();
                    onSelect({
                      id: instance.id,
                      elementId: instance.element_id,
                    });
                  }}
                />
              )}
              <line
                x1={percent(from.x)}
                y1={percent(from.y)}
                x2={percent(to.x)}
                y2={percent(to.y)}
                // Black, like the artwork it lies on: a leader is part of the filed drawing
                // and is drawn in ink there. Only the one the toolbar is acting on takes a
                // colour, which is screen state rather than anything that will be filed.
                className={chosen ? "stroke-primary" : "stroke-foreground"}
                strokeWidth={chosen ? 2 : 1.5}
                strokeLinecap="round"
                // Only the segment that reaches the part, so a flattened curve carries one
                // head rather than one per segment.
                markerEnd={
                  headed && last
                    ? `url(#${chosen ? HEAD_IDS.chosen : HEAD_IDS.plain})`
                    : undefined
                }
              />
            </g>
          );
        });
      })}
    </svg>
  );
}
